import Big from 'big.js'
import {format} from 'date-fns'

import FlightArrowSvg from '@/assets/flight/icons/flight-arrow.svg?react'
import Loading from '@/components/Loading/Loading'
import Price from '@/components/Price'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import {USD_DISPLAY_DECIMALS} from '@/constants/config'
import Pagination from '@/layout/Pagination'
import {BookingStatus} from '@/lib/flight/flightModel'
import {getBookings} from '@/lib/flight/flightService'
import HeadTags from '@/lib/head/HeadTags'
import {useLocale} from '@/lib/locale/locale'
import {FlightsDetailsRoute, FlightsRoute} from '@/routeRegistry'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import useWindowSize from '@/utils/hooks/useWindowSize'
import {NO_REFETCH_OPTIONS} from '@/utils/query/constants'
import useSearch from '@/utils/router/useSearch'

import style from './Flights.module.scss'

const ITEMS_PER_PAGE = 20
const ITEMS_PER_PAGE_MOBILE = 10

function useBookingsQuery() {
  const [locale] = useLocale()
  return useQuery({
    queryKey: ['bookings', locale],
    queryFn: async ({signal}) => getBookings({signal, locale}),
    ...NO_REFETCH_OPTIONS,
  })
}

export const STATUS_MAPPING: Record<
  BookingStatus,
  {
    text: string
    color: string
  }
> = {
  [BookingStatus.AWAITING_PAYMENT]: {
    text: 'Awaiting payment',
    color: '#e6b700',
  },
  [BookingStatus.FAILED]: {
    text: 'Failed',
    color: '#cd2732',
  },
  [BookingStatus.CONFIRMED]: {
    text: 'Confirmed',
    color: '#0f8a40',
  },
  [BookingStatus.TICKET_ISSUED]: {
    text: 'Ticket issued',
    color: '#0492b3',
  },
  [BookingStatus.COMPLETED]: {
    text: 'Completed',
    color: '#5d5d5d',
  },
  [BookingStatus.CANCELLATION_REQUESTED]: {
    text: 'Cancellation requested',
    color: '#8a0f6b',
  },
  [BookingStatus.CANCELLED]: {
    text: 'Cancelled',
    color: '#e6b700',
  },
  [BookingStatus.REFUNDED]: {
    text: 'Refunded',
    color: '#0085e6',
  },
}

export default memo(function Bookings() {
  const {data, isLoading} = useBookingsQuery()
  const navigate = useNavigate()
  const router = useRouter()
  const {isMobile} = useWindowSize()

  const [page, setPage] = useSearch(FlightsRoute.id, 'page', {
    replace: false,
    resetScroll: true,
    hashScrollIntoView: {
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    },
  })

  const filteredBookings =
    data?.filter(
      booking => ![BookingStatus.AWAITING_PAYMENT, BookingStatus.FAILED].includes(booking.status),
    ) ?? []

  const totalItems = filteredBookings.length

  const itemsPerPage = isMobile ? ITEMS_PER_PAGE_MOBILE : ITEMS_PER_PAGE
  const pageCount = totalItems > 0 ? Math.ceil(totalItems / itemsPerPage) : 1

  const currentPageItems = (() => {
    if (!totalItems) return []
    const startIndex = (page - 1) * itemsPerPage
    return filteredBookings.slice(startIndex, startIndex + itemsPerPage)
  })()

  useEffect(
    function correctPage() {
      if (!data) return
      if (pageCount > 0 && page > pageCount) {
        void setPage(pageCount)
      }
    },
    [page, pageCount, data],
  )

  const handlePageChange = useCallback((event: {selected: number}) => {
    void setPage(event.selected + 1)
  }, [])

  const hrefBuilder = useCallback(
    (pageNum: number) =>
      router.buildLocation({
        to: FlightsRoute.fullPath,
        search: {page: pageNum},
      }).href,
    [router],
  )

  return (
    <>
      <HeadTags title={`Flights (${totalItems})`} />
      <h1 className={style.Heading}>Flights ({totalItems})</h1>
      <div className={style.Bookings}>
        {isLoading && <Loading label='Loading flights...' />}
        {data && data.length === 0 && `No flights.`}
        {currentPageItems.map(booking => {
          const numOfAdult = booking.passengers.filter(pax => pax.type === 'adult').length
          const numOfChild = booking.passengers.filter(pax => pax.type === 'child').length
          const numOfInfantInSeat = booking.passengers.filter(
            pax => pax.type === 'infant_in_seat',
          ).length
          const numOfInfantOnLap = booking.passengers.filter(
            pax => pax.type === 'infant_on_lap',
          ).length
          const passengerText = (() => {
            const texts = []
            if (numOfAdult > 0) texts.push(`${numOfAdult} adult${numOfAdult > 1 ? 's' : ''}`)
            if (numOfChild > 0) texts.push(`${numOfChild} child${numOfChild > 1 ? 'ren' : ''}`)
            if (numOfInfantInSeat > 0)
              texts.push(`${numOfInfantInSeat} infant${numOfInfantInSeat > 1 ? 's' : ''} in seat`)
            if (numOfInfantOnLap > 0)
              texts.push(`${numOfInfantOnLap} infant${numOfInfantOnLap > 1 ? 's' : ''} on lap`)
            return texts.join(', ')
          })()

          const cityText = (() => {
            const cities: string[] = []
            booking.flightSegments.forEach(fs => {
              fs.flights.forEach(f => {
                const lastCity = cities[cities.length - 1]
                if (lastCity !== f.departure_airport.city) cities.push(f.departure_airport.city)
                if (lastCity !== f.arrival_airport.city) cities.push(f.arrival_airport.city)
              })
            })

            if (cities[cities.length - 1] === cities[0]) {
              cities.pop()
            }
            return cities.join(' - ')
          })()

          const price = Big(booking.price)
          const discountValue = Big(booking.moreInfo.amountDiscount).times(-1)
          const roundUp = Big(booking.moreInfo.amountRoundUp)
          const total = price.add(discountValue).add(roundUp)

          return (
            <div key={booking.id} className={style.Booking}>
              <div className={style.BookingHeader}>
                <div>
                  {/* TODO: Add time left later */}
                  {/* <div>In 8 days</div> */}
                  <div className={style.BookingNumber}>
                    Booking number <span>{booking.id}</span>
                  </div>
                  <h2 className={style.BookingDate}>
                    Placed on {format(new Date(booking.createdAt), 'dd MMM, yyyy')}
                  </h2>
                </div>
                <div className={style.BookingStatus}>
                  <div style={{color: STATUS_MAPPING[booking.status].color}}>
                    {STATUS_MAPPING[booking.status].text}
                  </div>
                </div>
              </div>
              <div className={style.BookingBody}>
                <div className={style.BookingBodyDetails}>
                  <div className={style.BookingBodyOverallInfo}>
                    <div>{cityText}</div>
                    <div className={style.BookingBodyOverallInfoSub}>{passengerText}</div>
                  </div>
                  <div className={style.FlightSegments}>
                    {booking.flightSegments.map((flightSegment, idx) => {
                      const firstFlight = flightSegment.flights[0]
                      const lastFlight = flightSegment.flights[flightSegment.flights.length - 1]

                      if (!firstFlight || !lastFlight) return null
                      const durationText = `${Math.floor(flightSegment.total_duration / 60)}h${flightSegment.total_duration % 60}m`

                      const numOfStops = flightSegment.flights.length - 1

                      return (
                        <div
                          key={idx}
                          className={style.FlightSegment}
                          role='region'
                          aria-label={`Flight ${idx + 1}`}
                        >
                          <VisuallyHidden as='h3'>
                            Flight {idx + 1}: From {firstFlight.departure_airport.id} to{' '}
                            {lastFlight.arrival_airport.id}
                          </VisuallyHidden>
                          <div className={style.FlightSegmentHeader}>
                            <img src={firstFlight.airline_logo} alt='' loading='lazy' />
                            <div className={style.FlightSegmentHeaderDetails}>
                              <div className={style.FlightSegmentHeaderDetailsTitle}>
                                <VisuallyHidden as='h4'>Airline:</VisuallyHidden>
                                {firstFlight.airline} -{' '}
                                <VisuallyHidden as='h4'>Travel class:</VisuallyHidden>
                                {firstFlight.travel_class}
                              </div>
                              <div className={style.FlightSegmentHeaderDetailsSubtitle}>
                                <VisuallyHidden as='h4'>Flight number:</VisuallyHidden>
                                {firstFlight.flight_number}
                              </div>
                            </div>
                          </div>
                          <div className={style.FlightSegmentBody}>
                            <div className={style.FlightSegmentBodyDestination}>
                              <div className={style.FlightSegmentBodyDestinationCity}>
                                <VisuallyHidden as='h4'>Departure airport:</VisuallyHidden>
                                {firstFlight.departure_airport.id}
                              </div>
                              <div className={style.FlightSegmentBodyDestinationTime}>
                                <VisuallyHidden as='h4'>Departure time:</VisuallyHidden>
                                {format(new Date(firstFlight.departure_airport.time), 'hh:mm a')}
                              </div>
                              <div className={style.FlightSegmentBodyDestinationDate}>
                                <VisuallyHidden as='h4'>Departure date:</VisuallyHidden>
                                {format(
                                  new Date(firstFlight.departure_airport.time),
                                  'MMM dd, yyyy',
                                )}
                              </div>
                            </div>
                            <div className={style.FlightSegmentBodyDuration}>
                              <div className={style.FlightSegmentBodyDurationText}>
                                <VisuallyHidden as='h4'>Duration:</VisuallyHidden>
                                {durationText}
                              </div>
                              <FlightArrowSvg aria-hidden />
                              <div className={style.FlightSegmentBodyDurationText}>
                                (
                                {numOfStops === 0
                                  ? 'Non-stop'
                                  : `${numOfStops} stop${numOfStops > 1 ? 's' : ''}`}
                                )
                              </div>
                            </div>
                            <div className={style.FlightSegmentBodyDestination}>
                              <div className={style.FlightSegmentBodyDestinationCity}>
                                <VisuallyHidden as='h4'>Arrival airport:</VisuallyHidden>
                                {lastFlight.arrival_airport.id}
                              </div>
                              <div className={style.FlightSegmentBodyDestinationTime}>
                                <VisuallyHidden as='h4'>Arrival time:</VisuallyHidden>
                                {format(new Date(lastFlight.arrival_airport.time), 'hh:mm a')}
                              </div>
                              <div className={style.FlightSegmentBodyDestinationDate}>
                                <VisuallyHidden as='h4'>Arrival date:</VisuallyHidden>
                                {format(new Date(lastFlight.arrival_airport.time), 'MMM dd, yyyy')}
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
              <div className={style.BookingFooter}>
                <div className={style.BookingFooterTotal}>
                  <div>Total</div>
                  <Price
                    price={total}
                    options={{
                      fractionDigits: USD_DISPLAY_DECIMALS,
                      exactFractionDigits: true,
                    }}
                  />
                </div>
                <div>
                  <PrimaryButton
                    size='md'
                    onPress={() => {
                      void navigate({
                        to: FlightsDetailsRoute.fullPath,
                        params: {bookingId: booking.id},
                        search: {},
                      })
                    }}
                  >
                    View details
                  </PrimaryButton>
                </div>
              </div>
            </div>
          )
        })}
        {pageCount > 1 && (
          <Pagination
            onPageChange={handlePageChange}
            pageCount={pageCount}
            currentPage={page - 1}
            hrefBuilder={hrefBuilder}
          />
        )}
      </div>
    </>
  )
})
